import { useState } from 'react';
import { api, useAction } from '../../../api/hooks';
import { Button } from '../../../ui/Button';
import { ErrorAlert } from '../../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../../ui/forms';
import { Modal } from '../../../ui/Modal';
import type { AttemptDetail } from '../types';
import { ChecksEditor } from './ChecksEditor';

export function ReportDialog({
  a,
  base,
  onClose,
  inv,
}: {
  a: AttemptDetail;
  base: string;
  onClose: () => void;
  inv: unknown[][];
}) {
  const b = a.binding;
  const [outcome, setOutcome] = useState<'success' | 'failure' | 'unknown'>('success');
  const [ref, setRef] = useState('');
  const [rev, setRev] = useState(b.sourceRevision ?? b.rollbackRevision ?? '');
  const [checks, setChecks] = useState<Record<string, { passed: boolean; observation: string }>>({});
  const [partial, setPartial] = useState('');
  const [errors, setErrors] = useState('');
  const m = useAction(
    () =>
      api.post(`${base}/report`, {
        expectedVersion: a.version,
        sourceRevision: rev,
        artifactDigest: b.artifactDigest,
        environmentId: b.environmentId,
        outcome,
        deploymentReference: ref || null,
        checks: Object.entries(checks).map(([name, r]) => ({ name, ...r })),
        partialEffects: partial || null,
        errors: errors || null,
      }),
    { success: 'Outcome recorded', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title={`Report ${a.attempt}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending}>
            Record outcome
          </Button>
        </>
      }
    >
      <Field label="Outcome">
        {(p) => (
          <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value as never)}>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="unknown">Unknown</option>
          </Select>
        )}
      </Field>
      <Field
        label="Deployed revision"
        hint="Must match the approved binding. If something else was deployed, report Unknown and describe it."
      >
        {(p) => <Input {...p} className="mono" value={rev} onChange={(e) => setRev(e.target.value)} />}
      </Field>
      <Field
        label="Execution reference or log link"
        hint="Required for success. A push or merge alone is not delivery."
      >
        {(p) => <Input {...p} value={ref} onChange={(e) => setRef(e.target.value)} />}
      </Field>
      <ChecksEditor checks={b.verificationChecks} value={checks} onChange={setChecks} />
      <Field label="Partial external effects" optional>
        {(p) => <Textarea {...p} rows={2} value={partial} onChange={(e) => setPartial(e.target.value)} />}
      </Field>
      <Field label="Errors" optional>
        {(p) => <Textarea {...p} rows={2} value={errors} onChange={(e) => setErrors(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
